'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';

interface FooterProps {
  className?: string;
}

const Footer = ({ className = '' }: FooterProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');

  useEffect(() => {
    setIsHydrated(true);
    const savedLanguage = localStorage.getItem('preferredLanguage') || 'fr';
    setCurrentLanguage(savedLanguage);
  }, []);

  const content = {
    fr: {
      tagline: 'Qualité suisse, service personnel',
      quickLinks: 'Liens rapides',
      services: 'Services',
      contact: 'Contact',
      address: 'Le Locle, Suisse',
      phone: '+41 76 212 65 06',
      email: 'servicesrouissi@gmail.com',
      hours: 'Lun-Ven: 8h-18h',
      hoursSat: 'Sam: 9h-16h',
      copyright: 'Tous droits réservés.',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions d\'utilisation',
    },
    de: {
      tagline: 'Schweizer Qualität, persönlicher Service',
      quickLinks: 'Schnelllinks',
      services: 'Dienstleistungen',
      contact: 'Kontakt',
      address: 'Le Locle, Schweiz',
      phone: '+41 76 212 65 06',
      email: 'servicesrouissi@gmail.com',
      hours: 'Mo-Fr: 8-18 Uhr',
      hoursSat: 'Sa: 9-16 Uhr',
      copyright: 'Alle Rechte vorbehalten.',
      privacy: 'Datenschutzrichtlinie',
      terms: 'Nutzungsbedingungen',
    },
  };

  const currentContent = isHydrated ? content[currentLanguage as keyof typeof content] : content.fr;
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { label: 'Accueil', href: '/homepage' },
    { label: 'Services', href: '/services' },
    { label: 'Devis', href: '/quote-request' },
    { label: 'À propos', href: '/about' },
    { label: 'Carrières', href: '/careers' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <footer className={`bg-foreground text-primary-foreground ${className}`}>
      <div className="container mx-auto px-4 py-12 lg:py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          {/* Brand Section */}
          <div>
            <Link href="/homepage" className="flex items-center space-x-2 mb-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg overflow-hidden">
                <Image
                  src="/assets/images/11958198-BEF2-462C-BE09-83DB57C854BC-1768765478440.png"
                  alt="Services Rouissi Logo"
                  width={40}
                  height={40}
                  className="object-contain"
                />
              </div>
              <span className="text-xl font-heading font-bold">Services Rouissi</span>
            </Link>
            <p className="text-sm text-primary-foreground/80 mb-4">
              {currentContent.tagline}
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-primary rounded-lg flex items-center justify-center transition-colors duration-300"
                aria-label="Facebook"
              >
                <Icon name="ShareIcon" size={20} variant="outline" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-primary rounded-lg flex items-center justify-center transition-colors duration-300"
                aria-label="Instagram"
              >
                <Icon name="CameraIcon" size={20} variant="outline" />
              </a>
              <a
                href="#"
                className="w-10 h-10 bg-primary-foreground/10 hover:bg-primary rounded-lg flex items-center justify-center transition-colors duration-300"
                aria-label="LinkedIn"
              >
                <Icon name="BriefcaseIcon" size={20} variant="outline" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-heading font-bold mb-4">{currentContent.quickLinks}</h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors duration-300"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-lg font-heading font-bold mb-4">{currentContent.services}</h3>
            <ul className="space-y-2 text-sm text-primary-foreground/80">
              <li>Nettoyage résidentiel</li>
              <li>Nettoyage commercial</li>
              <li>Services de conciergerie</li>
              <li>Nettoyages spéciaux</li>
              <li>Multi-services</li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-heading font-bold mb-4">{currentContent.contact}</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-2 text-sm text-primary-foreground/80">
                <Icon name="MapPinIcon" size={20} variant="outline" className="flex-shrink-0 mt-0.5" />
                <span>{currentContent.address}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-primary-foreground/80">
                <Icon name="PhoneIcon" size={20} variant="outline" className="flex-shrink-0 mt-0.5" />
                <a href={`tel:${currentContent.phone.replace(/\s/g, '')}`} className="hover:text-primary-foreground transition-colors duration-300">
                  {currentContent.phone}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-primary-foreground/80">
                <Icon name="EnvelopeIcon" size={20} variant="outline" className="flex-shrink-0 mt-0.5" />
                <a href={`mailto:${currentContent.email}`} className="hover:text-primary-foreground transition-colors duration-300">
                  {currentContent.email}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-primary-foreground/80">
                <Icon name="ClockIcon" size={20} variant="outline" className="flex-shrink-0 mt-0.5" />
                <div>
                  <div>{currentContent.hours}</div>
                  <div>{currentContent.hoursSat}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-primary-foreground/20 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-2">
              <Image
                src="/assets/images/11958198-BEF2-462C-BE09-83DB57C854BC-1768765478440.png"
                alt="Services Rouissi Logo"
                width={24}
                height={24}
                className="object-contain"
              />
              <p className="text-sm text-primary-foreground/80">
                &copy; {currentYear} Services Rouissi. {currentContent.copyright}
              </p>
            </div>
            <div className="flex gap-6">
              <a href="#" className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors duration-300">
                {currentContent.privacy}
              </a>
              <a href="#" className="text-sm text-primary-foreground/80 hover:text-primary-foreground transition-colors duration-300">
                {currentContent.terms}
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;